 /* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState, useCallback, memo } from 'react';
import {
    Box,
    Flex,
    Button,
    Text,
    Badge,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useDisclosure,
    useBreakpointValue,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
// import { isEmpty } from 'lodash';

import FilterSlider from './FilterSlider';
import CheckList from './CheckList';
import { useVendors, normalizeVendor } from 'hooks/useVendors';
import { ApiEndpoints } from 'services/endpoints';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — matches the rest of the app
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const FONT = "'Bricolage Grotesque', sans-serif";

// const SHADOW_MD     = '0 4px 16px rgba(27,59,95,0.08)';
const SHADOW_LG     = '0 12px 32px rgba(27,59,95,0.12)';
const NAVY_ACTIVE_BG = 'rgba(27, 59, 95, 0.08)';

const INITIAL_SLIDER_VALUE = 0;

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */

const FilterIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 5h18M6 12h12M10 19h4"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const ChevronDownIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M6 9l6 6 6-6"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   FILTER PANEL HEADER — used in both desktop and mobile
   ───────────────────────────────────────────────────────────── */

const FilterHeader = memo(function FilterHeader({ count, onClose, showCloseButton }) {
    return (
        <Flex
            align="center"
            justify="space-between"
            p="14px 16px"
            borderBottom="1px solid"
            borderColor={BORDER}
            bg={SURFACE_ALT}
        >
            <Flex align="center" gap="8px">
                <Text fontSize="13px" fontWeight="800" color={INK} fontFamily={FONT}>
                    Filter products
                </Text>
                {count > 0 && (
                    <Badge
                        bg={NAVY}
                        color="white"
                        fontSize="9px"
                        fontWeight="800"
                        px="6px"
                        py="2px"
                        borderRadius="full"
                    >
                        {count}
                    </Badge>
                )}
            </Flex>
            {showCloseButton && (
                <IconButton
                    aria-label="Close filters"
                    icon={<CloseIcon />}
                    onClick={onClose}
                    variant="ghost"
                    size="sm"
                    h="28px"
                    minW="28px"
                    color={MUTED}
                    _hover={{ bg: SURFACE, color: INK }}
                />
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   FILTER SECTION — wraps an accordion item with a count badge
   ───────────────────────────────────────────────────────────── */

const FilterSection = memo(function FilterSection({ name, count, children }) {
    return (
        <AccordionItem border="none" borderBottom="1px solid" borderColor={BORDER}>
            <AccordionButton
                py="14px"
                px="16px"
                _hover={{ bg: SURFACE_ALT }}
                _expanded={{ bg: SURFACE_ALT }}
                _focusVisible={{
                    boxShadow: `inset 0 0 0 2px ${NAVY}`,
                    outline: 'none',
                }}
                bg={SURFACE}
            >
                <Flex flex="1" align="center" gap="8px" textAlign="left">
                    <Text fontSize="13px" fontWeight="600" color={INK} fontFamily={FONT}>
                        {name}
                    </Text>
                    {count > 0 && (
                        <Badge
                            bg={CYAN}
                            color={NAVY}
                            fontSize="9px"
                            fontWeight="800"
                            px="5px"
                            py="1px"
                            borderRadius="full"
                        >
                            {count}
                        </Badge>
                    )}
                </Flex>
                <Box color={FAINT}>
                    <ChevronDownIcon />
                </Box>
            </AccordionButton>
            <AccordionPanel
                pb="14px"
                px="16px"
                pt="4px"
                maxH="240px"
                overflowY="auto"
                overflowX="hidden"
                bg={SURFACE}
            >
                {children}
            </AccordionPanel>
        </AccordionItem>
    );
});

/* ─────────────────────────────────────────────────────────────
   FOOTER — Reset + Apply buttons
   ───────────────────────────────────────────────────────────── */

const FilterFooter = memo(function FilterFooter({
    count, hasActive, onReset, onApply,
}) {
    return (
        <Flex
            justify="space-between"
            align="center"
            gap="10px"
            p="12px 16px"
            borderTop="1px solid"
            borderColor={BORDER}
            bg={SURFACE_ALT}
        >
            <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                isDisabled={!hasActive}
                color={MUTED}
                fontWeight="600"
                fontSize="12px"
                fontFamily={FONT}
                _hover={{ bg: SURFACE, color: INK }}
                _active={{ bg: BORDER }}
                _disabled={{
                    color: FAINT,
                    cursor: 'not-allowed',
                    opacity: 0.5,
                }}
                borderRadius="8px"
                h="36px"
                px="12px"
            >
                Reset all
            </Button>
            <Button
                onClick={onApply}
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                _active={{ bg: NAVY_DEEP }}
                fontSize="12px"
                fontWeight="700"
                fontFamily={FONT}
                borderRadius="8px"
                h="36px"
                px="16px"
                rightIcon={
                    count > 0 ? (
                        <Box
                            bg="whiteAlpha.300"
                            px="6px"
                            py="1px"
                            borderRadius="full"
                            fontSize="10px"
                            fontWeight="800"
                        >
                            {count}
                        </Box>
                    ) : undefined
                }
            >
                Apply
            </Button>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

export default function FilterComponent({ handleFilter }) {
    const { isOpen, onOpen, onClose } = useDisclosure();

    /* Use Drawer (bottom sheet) on mobile, Menu (dropdown) on desktop */
    const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

    const [selectedBrands,  setSelectedBrands]  = useState([]);
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [sliderValue,     setSliderValue]     = useState(INITIAL_SLIDER_VALUE);
    const [resetKey,        setResetKey]        = useState(0);
    const [expandedIndex,   setExpandedIndex]   = useState(0); /* default: Price open */

    const { brands } = useSelector((state) => state.productReducer);
    // const { vendors, fetchVendors } = useVendors();
    const { vendors: rawVendors } = useVendors({ per_page: 500 });
    const vendors = useMemo(
    () => rawVendors.map((v) => ({
        ...normalizeVendor(v),
        name: normalizeVendor(v).name, /* CheckList reads .name */
    })),
    [rawVendors]
    );

    /* Lazy-load vendor list */
    // useEffect(() => {
    //     if (isEmpty(vendors)) {
    //         fetchVendors();
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [vendors]);

    /* Count how many filter CATEGORIES are touched (matches the original) */
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (selectedBrands.length > 0)  count += 1;
        if (selectedVendors.length > 0) count += 1;
        if (sliderValue > INITIAL_SLIDER_VALUE) count += 1;
        return count;
    }, [selectedBrands, selectedVendors, sliderValue]);

    const hasActiveFilters = activeFilterCount > 0;

    /* Apply — bubble filters up and close */
    const applyFilter = useCallback(() => {
        handleFilter?.({
            selectedVendors,
            selectedColors: [], /* preserved for API compatibility */
            selectedBrands,
            sliderValue,
        });
        onClose();
    }, [handleFilter, selectedVendors, selectedBrands, sliderValue, onClose]);

    /* Reset — clear everything and notify parent */
    const resetFilters = useCallback(() => {
        setResetKey((k) => k + 1);
        setSelectedBrands([]);
        setSelectedVendors([]);
        setSliderValue(INITIAL_SLIDER_VALUE);
        handleFilter?.({
            selectedVendors: [],
            selectedColors: [],
            selectedBrands: [],
            sliderValue: INITIAL_SLIDER_VALUE,
        });
    }, [handleFilter]);

    /* Sections configuration — keeps render markup compact */
    const sections = useMemo(
        () => [
            {
                id: 'price',
                name: 'Price',
                count: sliderValue > INITIAL_SLIDER_VALUE ? 1 : 0,
                content: (
                    <FilterSlider
                        key={`${resetKey}-price`}
                        sliderValue={sliderValue}
                        setSliderValue={setSliderValue}
                    />
                ),
            },
            {
                id: 'brand',
                name: 'Brand',
                count: selectedBrands.length,
                content: (
                    <CheckList
                        key={`${resetKey}-brand`}
                        listItems={brands ?? []}
                        selected={selectedBrands}
                        setSelected={setSelectedBrands}
                        selectOne
                    />
                ),
            },
            {
                id: 'vendor',
                name: 'Vendors',
                count: selectedVendors.length,
                content: (
                    <CheckList
                        key={`${resetKey}-vendor`}
                        listItems={vendors ?? []}
                        selected={selectedVendors}
                        setSelected={setSelectedVendors}
                        selectOne
                        searchApi={`${ApiEndpoints.GET_CUSTOMERS}?contactType=vendor&cf_user_type=B2B&contact_name`}
                    />
                ),
            },
        ],
        [
            resetKey,
            sliderValue,
            selectedBrands,
            selectedVendors,
            brands,
            vendors,
        ]
    );

    /* ── Shared filter body — same UI on mobile + desktop ── */
    const filterBody = (
        <>
            <FilterHeader
                count={activeFilterCount}
                onClose={onClose}
                showCloseButton={isMobile}
            />
            <Accordion
                allowToggle
                index={expandedIndex}
                onChange={(idx) => setExpandedIndex(idx)}
            >
                {sections.map((sec) => (
                    <FilterSection key={sec.id} name={sec.name} count={sec.count}>
                        {sec.content}
                    </FilterSection>
                ))}
            </Accordion>
            <FilterFooter
                count={activeFilterCount}
                hasActive={hasActiveFilters}
                onReset={resetFilters}
                onApply={applyFilter}
            />
        </>
    );

    /* ── Trigger button (shared across both viewports) ── */
    const triggerButton = (
        <Button
            onClick={onOpen}
            leftIcon={<FilterIcon />}
            bg={hasActiveFilters ? NAVY : SURFACE}
            color={hasActiveFilters ? 'white' : INK}
            border="1px solid"
            borderColor={hasActiveFilters ? NAVY : BORDER}
            _hover={{
                bg: hasActiveFilters ? NAVY_DEEP : NAVY_ACTIVE_BG,
                borderColor: hasActiveFilters ? NAVY_DEEP : NAVY,
            }}
            _active={{
                bg: hasActiveFilters ? NAVY_DEEP : SURFACE_ALT,
            }}
            borderRadius="50px"
            h="38px"
            px="14px"
            fontSize="12.5px"
            fontWeight="700"
            fontFamily={FONT}
            aria-label={
                hasActiveFilters
                    ? `Filters (${activeFilterCount} active)`
                    : 'Open filters'
            }
            transition="all 0.15s ease"
        >
            <Flex align="center" gap="8px">
                <Text as="span">Filter</Text>
                {hasActiveFilters && (
                    <Badge
                        bg="whiteAlpha.300"
                        color="white"
                        fontSize="10px"
                        fontWeight="800"
                        px="6px"
                        py="0"
                        borderRadius="full"
                        minW="18px"
                        textAlign="center"
                    >
                        {activeFilterCount}
                    </Badge>
                )}
            </Flex>
        </Button>
    );

    /* ── Mobile: bottom sheet via Drawer ── */
    if (isMobile) {
        return (
            <>
                {triggerButton}
                <Drawer
                    isOpen={isOpen}
                    onClose={onClose}
                    placement="bottom"
                    autoFocus={false}
                >
                    <DrawerOverlay bg="blackAlpha.500" />
                    <DrawerContent
                        borderTopRadius="20px"
                        bg={SURFACE}
                        fontFamily={FONT}
                        maxH="85vh"
                    >
                        <DrawerBody p="0" display="flex" flexDirection="column">
                            {/* Drag handle — visual affordance */}
                            <Flex justify="center" pt="10px" pb="4px" flexShrink={0}>
                                <Box
                                    w="40px"
                                    h="4px"
                                    bg={BORDER}
                                    borderRadius="full"
                                />
                            </Flex>
                            <Box flex="1" overflowY="auto">
                                {filterBody}
                            </Box>
                        </DrawerBody>
                    </DrawerContent>
                </Drawer>
            </>
        );
    }

    /* ── Desktop: anchored Menu dropdown ── */
    return (
        <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose} closeOnSelect={false}>
            <MenuButton as={Box}>{triggerButton}</MenuButton>
            <MenuList
                p="0"
                w="360px"
                maxW="92vw"
                borderRadius="14px"
                border="1px solid"
                borderColor={BORDER}
                boxShadow={SHADOW_LG}
                overflow="hidden"
                fontFamily={FONT}
                bg={SURFACE}
            >
                {filterBody}
            </MenuList>
        </Menu>
    );
}